import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import {
    AttachmentsResource,
    EntityTypeEnumType,
    LinkTypesEnum,
    GetAttachmentsResponse,
} from '@mark43/cad-api';
import { ATTACHMENTS_QUERY_KEY } from '../constants';
import { AttachmentsPanelType } from '../types';
import { useEnabledFeatures } from '~/common/hooks/enabled-features';

type UseAttachmentsBaseParameter = {
    entityId: number;
    entityType: AttachmentsPanelType;
    entityNumber?: string;
    /**
     * Whether to refetch on mount.
     * - true: Refetch if data is stale (default)
     * - false: Never refetch on mount, use cached data if available
     */
    refetchOnMount?: boolean;
};

export function useAttachments({
    entityId,
    entityType,
    refetchOnMount = true,
}: UseAttachmentsBaseParameter) {
    const apiClient = useApiClient();
    const { mediaAttachmentsEnabled } = useEnabledFeatures();

    return useQuery<GetAttachmentsResponse>({
        queryKey: [ATTACHMENTS_QUERY_KEY, entityId],
        queryFn: async (): Promise<GetAttachmentsResponse> => {
            const response = await apiClient.request<AttachmentsResource.GetAttachmentsForEntities>(
                {
                    method: 'POST',
                    url: 'attachments/get_attachments',
                    data: {
                        entityId,
                        entityType: entityType as EntityTypeEnumType,
                        linkTypeIds: [
                            entityType === 'BOLO'
                                ? LinkTypesEnum.BOLO_ATTACHMENT
                                : LinkTypesEnum.CAD_AGENCY_EVENT_ATTACHMENT,
                        ],
                    },
                }
            );
            return {
                attachments: response.data?.data?.attachments || [],
                mergedAttachments: response.data?.data?.mergedAttachments || [],
            };
        },
        enabled: mediaAttachmentsEnabled && !!entityId,
        refetchOnMount,
        // `fileWebServerPath` returned with attachments expires every 50 minutes.
        // Refetch every 30 minutes to keep a fresh path for preview/download,
        // avoiding failures due to an expired URL.
        refetchInterval: 30 * 60 * 1000,
    });
}
